/**
 * Regras puras do form da base de conhecimento: rótulos para leigo, validação (espelha os
 * limites do `CreateKnowledgeEntryDto` — spec 2026-10-08 §2), montagem do body de criação e
 * o diff do PATCH parcial.
 *
 * O servidor normaliza as palavras-chave (minúscula, sem acento, trim, dedupe) porque o
 * `selectKnowledge` casa por substring normalizada. Aqui só se faz trim + dedupe por termo
 * dobrado, para a pessoa não ver duas chips que o servidor fundiria numa.
 */
import { VERTICAL_LABELS } from '../../personas/persona-labels';
import { foldTerm } from '../../spotlight/fold-term';

import type {
  CreateKnowledgeEntryBody,
  KnowledgeEntryDto,
  KnowledgeKind,
  KnowledgeVertical,
  UpdateKnowledgeEntryBody,
} from './types';

export const KIND_LABELS: Record<KnowledgeKind, string> = {
  fact: 'Fato',
  rule: 'Conduta',
};

export const KIND_EXPLICACAO: Record<KnowledgeKind, string> = {
  fact: 'informação que a IA pode afirmar ao cliente',
  rule: 'como a IA deve agir',
};

export const VERTICAIS = Object.keys(VERTICAL_LABELS) as KnowledgeVertical[];

export function contextoLabel(vertical: KnowledgeVertical | null): string {
  return vertical === null ? 'Sempre' : `Só em ${VERTICAL_LABELS[vertical]}`;
}

export const LIMITES = {
  tituloMin: 2,
  tituloMax: 200,
  textoMax: 8000,
  palavrasMax: 50,
  palavraMax: 80,
  prioridadeMax: 1000,
} as const;

export interface KnowledgeFormValues {
  title: string;
  body: string;
  kind: KnowledgeKind;
  keywords: string[];
  /** String porque vem de `<input type="number">` — validada antes de virar número. */
  priority: string;
  enabled: boolean;
  vertical: KnowledgeVertical | null;
}

export type KnowledgeFormErrors = Partial<
  Record<'title' | 'body' | 'keywords' | 'priority', string>
>;

export const FORM_VAZIO: KnowledgeFormValues = {
  title: '',
  body: '',
  kind: 'fact',
  keywords: [],
  priority: '0',
  enabled: true,
  vertical: null,
};

export function formFromEntry(e: KnowledgeEntryDto): KnowledgeFormValues {
  return {
    title: e.title,
    body: e.body,
    kind: e.kind,
    keywords: [...e.keywords],
    priority: String(e.priority),
    enabled: e.enabled,
    vertical: e.vertical,
  };
}

/** Acrescenta uma palavra-chave (trim; ignora vazia, duplicada por termo dobrado e longa demais). */
export function addKeyword(atuais: readonly string[], nova: string): string[] {
  const v = nova.trim();
  if (v === '' || v.length > LIMITES.palavraMax) return [...atuais];
  const f = foldTerm(v);
  if (atuais.some((a) => foldTerm(a) === f)) return [...atuais];
  return [...atuais, v];
}

export function validateKnowledgeForm(v: KnowledgeFormValues): KnowledgeFormErrors {
  const errors: KnowledgeFormErrors = {};
  const title = v.title.trim();
  if (title.length < LIMITES.tituloMin) {
    errors.title = `O título precisa de pelo menos ${String(LIMITES.tituloMin)} caracteres.`;
  } else if (title.length > LIMITES.tituloMax) {
    errors.title = `O título pode ter no máximo ${String(LIMITES.tituloMax)} caracteres.`;
  }
  const body = v.body.trim();
  if (body.length === 0) {
    errors.body = 'Escreva o texto da entrada — é ele que a IA lê.';
  } else if (body.length > LIMITES.textoMax) {
    errors.body = `O texto pode ter no máximo ${String(LIMITES.textoMax)} caracteres.`;
  }
  if (v.keywords.length > LIMITES.palavrasMax) {
    errors.keywords = `No máximo ${String(LIMITES.palavrasMax)} palavras-chave.`;
  }
  const p = Number(v.priority);
  if (v.priority.trim() === '' || !Number.isInteger(p) || p < 0 || p > LIMITES.prioridadeMax) {
    errors.priority = `Use um número inteiro de 0 a ${String(LIMITES.prioridadeMax)}.`;
  }
  return errors;
}

export function buildCreateBody(v: KnowledgeFormValues): CreateKnowledgeEntryBody {
  return {
    title: v.title.trim(),
    body: v.body.trim(),
    kind: v.kind,
    keywords: [...v.keywords],
    priority: Number(v.priority),
    enabled: v.enabled,
    vertical: v.vertical,
  };
}

/** PATCH parcial: só os campos que diferem do original. Objeto vazio ⇒ nada mudou. */
export function buildUpdateBody(
  original: KnowledgeEntryDto,
  v: KnowledgeFormValues,
): UpdateKnowledgeEntryBody {
  const next = buildCreateBody(v);
  const diff: UpdateKnowledgeEntryBody = {};
  if (next.title !== original.title) diff.title = next.title;
  if (next.body !== original.body) diff.body = next.body;
  if (next.kind !== original.kind) diff.kind = next.kind;
  if (
    next.keywords.length !== original.keywords.length ||
    next.keywords.some((k, i) => k !== original.keywords[i])
  ) {
    diff.keywords = next.keywords;
  }
  if (next.priority !== original.priority) diff.priority = next.priority;
  if (next.enabled !== original.enabled) diff.enabled = next.enabled;
  if (next.vertical !== original.vertical) diff.vertical = next.vertical;
  return diff;
}

/** Casa título, texto e palavras-chave, sem diferenciar caixa nem acento. */
export function matchesSearch(e: KnowledgeEntryDto, termo: string): boolean {
  const f = foldTerm(termo);
  if (f === '') return true;
  return (
    foldTerm(e.title).includes(f) ||
    foldTerm(e.body).includes(f) ||
    e.keywords.some((k) => foldTerm(k).includes(f))
  );
}
